import React, { useCallback, useEffect, useRef, useState } from "react";
import { KontaktSearchQuery } from "../../../ts/CP";
import Header from "./Header";
import Kontakty from "./Kontakty";
import "./KontaktyWrapper.scss";

import { useNavigate, useParams } from "react-router";
import { kontaktModel } from "../../../api/models/kontaktModel";
import { useSimplePopups } from "../../../providers/SimplePopups/SimplePopupProvider";
import { SimplePopupType } from "../../../providers/SimplePopups/data";
import { useQueryWithPopupErrorHandling } from "../../../providers/utils/hooks";
import { useSelector } from "../../../redux/store";
import { Kontakt, Kontakt as KontaktType } from "../../../ts/Kontakt";
import { ErrorKey, serverErrorOfKey } from "../../../types/server-api/errors";
import Loading from "../../ui/Loading/Loading";
import SeznamKontaktu from "./SeznamKontaktu";

const KontaktyWrapperContext = React.createContext<
  | [
      value: KontaktType | undefined,
      setValue: (kontakt: KontaktType) => void,
      setIndex: (value: number) => void,
      addNew: (value: KontaktType) => void
    ]
  | []
>([]);

const EditableKontaktContext = React.createContext<
  [
    editable: boolean,
    setEditable: (editable: boolean) => void,
    newKontakt: boolean,
    setNewKontakt: (state: boolean) => void,
    listShown: boolean,
    setListShown: (state: boolean) => void
  ]
>([false, () => null, false, () => null, false, () => null]);

export const useKontakty = () => {
  return React.useContext(KontaktyWrapperContext);
};

export const useEditableKontakt = () => {
  return React.useContext(EditableKontaktContext);
};

interface Props {}

interface SeznamKontaktuRef {
  search(query: KontaktSearchQuery): void;
}

const KontaktyWrapper: React.FC<Props> = ({}: Props) => {
  const { id } = useParams();

  const [index, setIndex] = useState<number>(0);
  const [editable, setEditable] = useState(false);
  const [listShown, setListShown] = useState(false);
  const [newKontakt, setNewKontakt] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const [query, setQuery] = useState<KontaktSearchQuery | null>(null);

  const navigate = useNavigate();

  const { createSimplePopup } = useSimplePopups();
  const { call } = useQueryWithPopupErrorHandling();
  const [data, setData] = useState<Kontakt | undefined>(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingKontakt, setIsLoadingKontakt] = useState(true);
  const [pageSize] = useState(1);
  const fetchedWithId = useRef<string | null>(null);

  const getKontakt = async (id?: string, preventNavigation?: boolean) => {
    await call(
      () =>
        kontaktModel.getKontaktWithIndex({
          id,
          offset: index,
          limit: pageSize.toString(),
        }),
      "Zobrazenie kontaktu nebolo úspešné",
      null,
      ({ kontakt, totalCount, index: newIndex }) => {
        setData(kontakt);
        setTotalCount(totalCount);

        if (index !== newIndex) {
          setIndex(newIndex);
        }

        if (preventNavigation) {
          return;
        }

        const url = `/kontakty/${kontakt._id}`;

        navigate(url, { replace: true });
      },
      () => {
        fetchedWithId.current = null;
        navigate("/kontakty");
      },
      true
    );
    setIsLoadingKontakt(false);
    setIsLoading(false);
  };

  useEffect(() => {
    if (id && !fetchedWithId.current) {
      fetchedWithId.current = id;
      getKontakt(id);
    }
  }, [id]);

  useEffect(() => {
    getKontakt();
  }, [index, pageSize]);

  const seznamKontaktu = useRef<SeznamKontaktuRef>(null);

  const handleSearch = (query: KontaktSearchQuery) => {
    setQuery(query);
    setListShown(true);
  };

  const editKontakt = useCallback(
    (kontakt: Kontakt) => {
      call(
        () => kontaktModel.editKontakt({ kontakt }),
        "Úprava kontaktu nebola úspešná",
        "Úprava kontaktu bola úspešná",
        () => {
          getKontakt(id, true);
          setEditable(false);
        }
      );
    },
    [id, index, pageSize]
  );

  const createKontakt = useCallback((kontakt: Kontakt) => {
    call(
      () => kontaktModel.createKontakt({ kontakt }),
      "Pridanie kontaktu nebolo úspešné",
      "Pridanie kontaktu bolo úspešné",
      (newKontakt) => {
        fetchedWithId.current = null;
        navigate(`/kontakty/${newKontakt._id}`);
        setNewKontakt(false);
      },
      (serverErrors) => {
        if (serverErrorOfKey(ErrorKey.KONTAKT_ALREADY_EXISTS, serverErrors)) {
          createSimplePopup(
            "Názov kontaktu je už obsadený",
            SimplePopupType.Error
          );
        }
      }
    );
  }, []);

  useEffect(() => {
    const cb = () => {
      fetchedWithId.current = null;
    };

    window.addEventListener("popstate", cb);

    return () => {
      window.removeEventListener("popstate", cb);
    };
  }, []);

  if (isLoading) {
    return <Loading text="Získávanie kontaktov zo servera" />;
  }

  return (
    <KontaktyWrapperContext.Provider
      value={[
        data,
        (kontakt: KontaktType) => editKontakt(kontakt),
        (value: number) =>
          setIndex(value > totalCount - 1 ? index : value < 0 ? index : value),
        (kontakt: KontaktType) => {
          createKontakt(kontakt);
        },
      ]}
    >
      <div className="kontakty-wrapper">
        <div className="title">Kontakty</div>
        <EditableKontaktContext.Provider
          value={[
            editable,
            setEditable,
            newKontakt,
            setNewKontakt,
            listShown,
            setListShown,
          ]}
        >
          <Header
            totalCount={totalCount}
            index={index}
            handleSearch={handleSearch}
            refreshKontakts={getKontakt}
            activeQuery={query}
            isLoading={isLoadingKontakt}
          />
          <Kontakty shown={!listShown} />
          <SeznamKontaktu
            query={query}
            setQuery={setQuery}
            ref={seznamKontaktu}
            shown={listShown}
            setListShown={setListShown}
            onSelectRow={() => {
              fetchedWithId.current = null;
              setListShown(false);
              window.scrollTo({ top: 0 });
            }}
          />
        </EditableKontaktContext.Provider>
      </div>
    </KontaktyWrapperContext.Provider>
  );
};

export default KontaktyWrapper;
